import { ChannelDoc, ChatMessageDoc } from "@kwp/contracts";
import { fromStored, paths } from "@kwp/firestore";
import { useQuery } from "@tanstack/react-query";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTRPC } from "../../lib/api";
import { useClients } from "../../lib/clients";
import {
  emptyMessages,
  isUnread,
  mergeChannelLists,
  mergeLive,
  mergeOlder,
  oldest,
  PAGE_SIZE,
  type Channel,
  type Member,
  type Message,
  type MessageState,
} from "./model";

/**
 * Firestore listeners for the chat screen (architecture §11): reads only, plus the one write
 * clients are allowed, the read marker. Rules are not filters, so every query carries the
 * constraint the rule checks (public channels; DMs I am a member of).
 */

function parseOrSkip<T>(parse: () => T): T | null {
  try {
    return parse();
  } catch (error) {
    console.error(error);
    return null;
  }
}

function toChannel(d: QueryDocumentSnapshot<DocumentData>): Channel | null {
  return parseOrSkip(() => ({ id: d.id, ...fromStored(ChannelDoc, d.data({ serverTimestamps: "estimate" }), d.ref.path) }));
}

function toMessage(d: QueryDocumentSnapshot<DocumentData>): Message | null {
  return parseOrSkip(() => ({ id: d.id, ...fromStored(ChatMessageDoc, d.data({ serverTimestamps: "estimate" }), d.ref.path) }));
}

const notNull = <T,>(v: T | null): v is T => v !== null;

export type Loadable<T> = { status: "loading" } | { status: "error"; error: Error } | { status: "ready"; data: T };

/** Public channels and my DMs in a workspace, live. */
export function useChannels(workspaceId: string, uid: string): Loadable<Channel[]> {
  const { firebase } = useClients();
  const [publicChannels, setPublic] = useState<Channel[] | null>(null);
  const [dms, setDms] = useState<Channel[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setPublic(null);
    setDms(null);
    setError(null);
    const col = collection(firebase.db, paths.channels(workspaceId));
    const stopPublic = onSnapshot(
      query(col, where("kind", "==", "channel")),
      (snap) => setPublic(snap.docs.map(toChannel).filter(notNull)),
      setError,
    );
    const stopDms = onSnapshot(
      query(col, where("memberUids", "array-contains", uid)),
      (snap) => setDms(snap.docs.map(toChannel).filter(notNull).filter((c) => c.kind === "dm")),
      setError,
    );
    return () => {
      stopPublic();
      stopDms();
    };
  }, [firebase.db, workspaceId, uid]);

  return useMemo(() => {
    if (error !== null) return { status: "error", error };
    if (publicChannels === null || dms === null) return { status: "loading" };
    return { status: "ready", data: mergeChannelLists(publicChannels, dms) };
  }, [error, publicChannels, dms]);
}

/** One channel or DM, live (the inbox opens a conversation by id, WP-054); null when it is gone or not mine. */
export function useChannel(workspaceId: string, channelId: string): Loadable<Channel | null> {
  const { firebase } = useClients();
  const [state, setState] = useState<Loadable<Channel | null>>({ status: "loading" });
  useEffect(() => {
    setState({ status: "loading" });
    return onSnapshot(
      doc(firebase.db, paths.channel(workspaceId, channelId)),
      (snap) =>
        setState({
          status: "ready",
          data: snap.exists() ? parseOrSkip(() => ({ id: snap.id, ...fromStored(ChannelDoc, snap.data({ serverTimestamps: "estimate" }), snap.ref.path) })) : null,
        }),
      (error) => setState(error.code === "permission-denied" ? { status: "ready", data: null } : { status: "error", error }),
    );
  }, [firebase.db, workspaceId, channelId]);
  return state;
}

/** My read marker per channel (`null`: never read); a channel is absent until its listener answers. */
export function useReadMarkers(workspaceId: string, channelIds: readonly string[], uid: string): ReadonlyMap<string, Date | null> {
  const { firebase } = useClients();
  const [markers, setMarkers] = useState<ReadonlyMap<string, Date | null>>(new Map());
  const key = [...channelIds].sort().join(",");

  useEffect(() => {
    const ids = key.length === 0 ? [] : key.split(",");
    const stops = ids.map((channelId) =>
      onSnapshot(
        doc(firebase.db, paths.readMarker(workspaceId, channelId, uid)),
        (snap) => {
          const value: unknown = snap.get("lastReadAt", { serverTimestamps: "estimate" });
          const at = value !== null && typeof value === "object" && "toDate" in value ? (value as { toDate(): Date }).toDate() : null;
          setMarkers((all) => new Map(all).set(channelId, at));
        },
        (e) => console.error(e),
      ),
    );
    return () => stops.forEach((stop) => stop());
  }, [firebase.db, workspaceId, uid, key]);

  return markers;
}

/** Workspace members (names for authors, the DM picker and @mention autocomplete). */
export function useMembers(workspaceId: string) {
  const trpc = useTRPC();
  const members = useQuery(trpc.workspaces.members.queryOptions({ workspaceId }));
  const list: Member[] = useMemo(() => (members.data === undefined ? [] : members.data), [members.data]);
  const byUid = useMemo(() => new Map(list.map((m) => [m.uid, m])), [list]);
  return { list, byUid, status: members.status };
}

/**
 * The open channel's messages: a live listener on the latest page, and older pages fetched on
 * demand with a cursor (PAGE_SIZE at a time). Mount it keyed by channel id.
 */
export function useMessages(workspaceId: string, channelId: string) {
  const { firebase } = useClients();
  const [state, setState] = useState<MessageState>(emptyMessages);
  const [loaded, setLoaded] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const snaps = useRef(new Map<string, QueryDocumentSnapshot<DocumentData>>());
  const stateRef = useRef(state);
  stateRef.current = state;

  const col = useMemo(() => collection(firebase.db, paths.chatMessages(workspaceId, channelId)), [firebase.db, workspaceId, channelId]);

  useEffect(() => {
    let first = true;
    return onSnapshot(
      query(col, orderBy("createdAt", "desc"), limit(PAGE_SIZE)),
      (snap) => {
        snap.docs.forEach((d) => snaps.current.set(d.id, d));
        const page = snap.docs.map(toMessage).filter(notNull);
        const initial = first;
        first = false;
        setState((s) => mergeLive(s, page, initial));
        setLoaded(true);
      },
      setError,
    );
  }, [col]);

  const loadOlder = useCallback(async () => {
    const current = stateRef.current;
    const from = oldest(current);
    const cursor = from === null ? undefined : snaps.current.get(from.id);
    if (!current.hasOlder || cursor === undefined) return;
    setLoadingOlder(true);
    try {
      const snap = await getDocs(query(col, orderBy("createdAt", "desc"), startAfter(cursor), limit(PAGE_SIZE)));
      snap.docs.forEach((d) => snaps.current.set(d.id, d));
      setState((s) => mergeOlder(s, snap.docs.map(toMessage).filter(notNull)));
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoadingOlder(false);
    }
  }, [col]);

  return { state, loaded, loadingOlder, loadOlder, error };
}

const MARK_READ_EVERY_MS = 3000;

/**
 * Writes `reads/{me}` = {lastReadAt: serverTimestamp()} while the channel is on screen, the tab
 * is visible and the window focused, and there is something unread; at most every 3 seconds.
 * `readAt` is `undefined` until the marker's listener has answered.
 */
export function useMarkRead(workspaceId: string, channelId: string, uid: string, lastMessageAt: Date | null, readAt: Date | null | undefined) {
  const { firebase } = useClients();
  const lastWrite = useRef(0);
  const lastMessageMs = lastMessageAt === null ? null : lastMessageAt.getTime();
  const readMs = readAt === undefined ? undefined : readAt === null ? null : readAt.getTime();

  useEffect(() => {
    if (readMs === undefined) return;
    let timer: number | undefined;
    const attempt = () => {
      window.clearTimeout(timer);
      if (document.visibilityState !== "visible" || !document.hasFocus()) return;
      if (!isUnread(lastMessageMs === null ? null : new Date(lastMessageMs), readMs === null ? null : new Date(readMs))) return;
      const wait = lastWrite.current + MARK_READ_EVERY_MS - Date.now();
      if (wait > 0) {
        timer = window.setTimeout(attempt, wait);
        return;
      }
      lastWrite.current = Date.now();
      setDoc(doc(firebase.db, paths.readMarker(workspaceId, channelId, uid)), { lastReadAt: serverTimestamp() }).catch((e: unknown) =>
        console.error("marking the channel read failed", e),
      );
    };
    attempt();
    window.addEventListener("focus", attempt);
    document.addEventListener("visibilitychange", attempt);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", attempt);
      document.removeEventListener("visibilitychange", attempt);
    };
  }, [firebase.db, workspaceId, channelId, uid, lastMessageMs, readMs]);
}
